import React from 'react';
import dayjs from 'dayjs';
import { Box, Skeleton, Typography } from '@mui/material';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import { statCardCircleSx, statCardSx } from './DepartmentStatCard';
import { formatCurrency, getDeptShortName } from '../utils/deptDisplay';

// The month's biggest revenue day, called out as a card of its own: the day, the amount, how big a share of the month so
// far it was, how it compares with an ordinary day, and the department that carried it. `best` comes from
// biggestRevenueDay() (utils/incomeInsights.js). `dense` is the tighter layout used when the column beside the table is
// too short for the roomy one (see useFitDensity).
const GOLD = 'linear-gradient(135deg, #F59E0B 0%, #B45309 100%)';
const QUIET = 'linear-gradient(135deg, #94A3B8 0%, #475569 100%)';

const caption = (best) =>
  [best.shareOfMonth !== null && `${best.shareOfMonth}% of the month so far`, best.multipleOfAverage !== null && `${best.multipleOfAverage}× the daily average`]
    .filter(Boolean)
    .join(' · ');

export default function BiggestDayCard({ best, loading, dense = false }) {
  if (loading) return <Skeleton variant="rectangular" height={dense ? 96 : 112} sx={{ borderRadius: '16px', flex: '0 0 auto' }} />;

  const lead = best?.lead && `Led by ${getDeptShortName(best.lead.deptId, best.lead.deptName || '')} · ${formatCurrency(best.lead.amount)}`;
  const title = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <EmojiEventsRoundedIcon fontSize="small" />
      <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.95 }}>
        Biggest revenue day
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ ...statCardSx, height: 'auto', flex: '0 0 auto', background: best ? GOLD : QUIET }}>
      <Box sx={statCardCircleSx} />
      {dense ? (
        <Box sx={{ position: 'relative', zIndex: 1, px: 1.75, py: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
            {title}
            {best && <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{formatCurrency(best.total)}</Typography>}
          </Box>
          {best ? (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{dayjs(best.date).format('ddd, D MMM')}</Typography>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.92, lineHeight: 1.35 }}>{caption(best)}</Typography>
              {lead && <Typography variant="caption" sx={{ display: 'block', opacity: 0.92, lineHeight: 1.35 }}>{lead}</Typography>}
            </>
          ) : (
            <Typography variant="body2" sx={{ mt: 0.25, opacity: 0.9 }}>No collection recorded yet this month</Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ position: 'relative', zIndex: 1, p: 1.75 }}>
          {title}
          {best ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mt: 0.5 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{dayjs(best.date).format('ddd, D MMM')}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{formatCurrency(best.total)}</Typography>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.92 }}>{caption(best)}</Typography>
              {lead && <Typography variant="caption" sx={{ display: 'block', opacity: 0.92 }}>{lead}</Typography>}
            </>
          ) : (
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>No collection recorded yet this month</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
